import { useEffect, useRef } from "react";
import { useDispatch, useStore } from "react-redux";

import { LIMIT_THRESHOLD_BLOCK } from "@/constants/dataset";
import { useAgentAnalysisResult } from "@/hooks/agenticAI/useAgentAnalysisResult";
import { useAgentAreaInput } from "@/hooks/agenticAI/useAgentAreaInput";
import { useAgentDatasetWeights } from "@/hooks/agenticAI/useAgentDatasetWeights";
import { useAgentGlobalAdmArea } from "@/hooks/agenticAI/useAgentGlobalAdmArea";
import { useAgentOutputAnalysis } from "@/hooks/agenticAI/useAgentOutputAnalysis";
import { useProgressiveDatasetAdd } from "@/hooks/agenticAI/useProgressiveDatasetAdd";
import { useDataset } from "@/hooks/map/useDataset";
import type { AppDispatch, RootState } from "@/store";
import { resetAnalysisConfigSlices } from "@/store/analysisConfigSnapshot";
import { selectInputRowCounts } from "@/store/selectors/spatialSelectors";
import {
  updateDatasetDisplayName,
  updateRawDatasetDisplayName
} from "@/store/slice/datasetSlice";
import type { SpatialAggregationType } from "@/types/common";
import { AnalysisStatus } from "@/types/dataset";

import type {
  AnalysisResultRef,
  SetWorkspaceDatasetsHandler,
  WorkspaceAdapter,
  WorkspaceDatasetErrorCode,
  WorkspaceDatasetResult
} from "@lokasi-ai-web-sdk/agent-ui";

import { AgentDatasetProposal, RowLimitError } from "./types";
import { toPendingDataset, toWorkspaceError } from "./utils";

type WorkspaceFailure = {
  code: WorkspaceDatasetErrorCode;
  message: string;
};

/**
 * One entry's failure. The key stays in the answer whatever happened to it: the
 * agent proposed it, and the SDK is what turns a thrown handler into failures
 * for the keys it never heard about.
 */
const failedResult = (
  entryKey: string,
  error: WorkspaceFailure
): WorkspaceDatasetResult => ({
  dataset_entry_key: entryKey,
  status: "failed",
  error
});

/**
 * Answers the dataset interrupts of the agent and keeps the map equal to the
 * workspace of the agent.
 *
 * The hook removes only the datasets that it added. Spatial Analysis uses the
 * same `dataset` slice. Thus a comparison with the full store would delete the
 * datasets that the user added.
 *
 * Each of the two areas has its own hook and its own record: the working area
 * uses `useAgentGlobalAdmArea`, and the analysis area uses `useAgentAreaInput`.
 * The analysis area is the catchment or the administrative selection of the
 * analysis.
 *
 * The output analysis and the dataset weights follow the datasets, because
 * adding one rebuilds every weight from defaults.
 *
 * Drawing a finished run's result grid is `useAgentAnalysisResult`'s, and is the
 * one path that answers the agent with a fact about the map rather than about a
 * dataset.
 */
export const useAgentWorkspaceAdapter = () => {
  const dispatch = useDispatch<AppDispatch>();
  const store = useStore<RootState>();
  const { handleRemoveDataset, handleClearDataset } = useDataset();
  const { addDatasetProgressively } = useProgressiveDatasetAdd();
  const { syncGlobalAdmArea, clearAppliedGlobalAdmArea } =
    useAgentGlobalAdmArea();
  const { syncAreaInput, clearAppliedArea } = useAgentAreaInput();
  const { syncOutputAnalysis } = useAgentOutputAnalysis();
  const { syncDatasetWeights } = useAgentDatasetWeights();
  const { loadAnalysisResult: drawAnalysisResult, clearAnalysisResult } =
    useAgentAnalysisResult();

  const lastWorkspaceIdRef = useRef<string | null>(null);

  /**
   * The active workspace's `analysis_result`. `AgenticAIBody` fills it from
   * the SDK state, because the sync gets only the configuration.
   */
  const analysisResultRef = useRef<AnalysisResultRef | null>(null);
  /**
   * The analysis this page already drew or tried to draw. A sync does not try
   * it again, so a failed draw (R4) waits for the user or a refresh.
   */
  const drawnAnalysisIdRef = useRef<string | null>(null);

  /**
   * dataset_entry_key -> the layer this hook put on the map for it. The
   * `datasetId` is what lets a snapshot tell a replacement from a repeat.
   */
  const agentDatasetsRef = useRef(
    new Map<
      string,
      {
        compositeId: string;
        spatialType: SpatialAggregationType;
        datasetId: string;
      }
    >()
  );
  const activeBatchAbortRef = useRef<AbortController | null>(null);

  // Stop an open stream when the page unmounts.
  useEffect(() => () => activeBatchAbortRef.current?.abort(), []);

  const getExistingRowCount = () =>
    Object.values(selectInputRowCounts(store.getState())).reduce<number>(
      (total, count) => total + (count ?? 0),
      0
    );

  const getNextPaletteIndex = () =>
    Object.keys(store.getState().dataset.datasets).length;

  const applyDataset = async (
    proposal: AgentDatasetProposal,
    paletteIndex: number,
    batchSignal: AbortSignal,
    rowBudget: { base: number; used: number }
  ): Promise<WorkspaceDatasetResult> => {
    const entryKey = proposal.dataset_entry_key;
    const datasetName = proposal.dataset_name ?? entryKey;

    const pending = toPendingDataset(proposal);

    if (!pending) {
      return failedResult(entryKey, {
        code: "ERR_REQUEST_INVALID",
        message: "The agent proposed a dataset without an id."
      });
    }

    // A key this hook already applied means a replacement: the entry's version
    // is new, but the place it occupies is not. The layer that is there now
    // stays until the new one is complete.
    const previous = agentDatasetsRef.current.get(entryKey);
    /**
     * The displaced entry's rows. They are still charged to the budget while
     * the swap is in flight, so a replacement measures the configuration it
     * produces rather than both versions of it.
     */
    const previousRows = previous
      ? (selectInputRowCounts(store.getState())[previous.compositeId] ?? 0)
      : 0;
    rowBudget.base -= previousRows;

    // Use a separate controller. The batch or the row-limit check can stop the
    // stream, and the report must show the correct cause.
    const streamController = new AbortController();
    const abortForBatch = () => streamController.abort();
    batchSignal.addEventListener("abort", abortForBatch, { once: true });

    let rowLimitError: RowLimitError | undefined;
    /** Set when the layer exists, thus a refusal can remove the layer. */
    let addedLayer:
      { compositeId: string; spatialType: SpatialAggregationType } | undefined;
    /** The rows that this dataset took from the budget of the batch. */
    let chargedRows = 0;

    try {
      const { compositeId, spatialType, totalRows } =
        await addDatasetProgressively({
          datasetId: pending.id,
          filterPayload: pending.filters,
          h3Configuration: pending.grid_config,
          signal: streamController.signal,
          index: paletteIndex,
          onAdded: (addedId, addedType) => {
            addedLayer = { compositeId: addedId, spatialType: addedType };
          },
          onRows: (deliveredRows) => {
            // Count the rows that arrived, not the `dataset_data:meta` header.
            // The header comes before the filters and gives a too large count.
            // Then the check refuses datasets that are small enough.
            rowBudget.used += deliveredRows - chargedRows;
            chargedRows = deliveredRows;

            if (rowBudget.base + rowBudget.used <= LIMIT_THRESHOLD_BLOCK)
              return;

            rowLimitError = new RowLimitError(
              `Loading ${datasetName} would exceed the ${LIMIT_THRESHOLD_BLOCK.toLocaleString()} row limit for one analysis.`
            );
            streamController.abort();
          }
        });

      // The replacement is complete, so the layer it displaces can go now. A
      // stream that failed above never reaches this, which is what keeps the
      // previous dataset on the map until its successor has landed.
      if (previous) {
        handleRemoveDataset(previous.compositeId, previous.spatialType);
        // The staged layer took the key's next free slot, so it carries the
        // numbering of a second copy. The entry it replaced owns the name.
        dispatch(
          updateDatasetDisplayName({
            datasetId: compositeId,
            displayName: datasetName
          })
        );
        dispatch(
          updateRawDatasetDisplayName({
            datasetId: compositeId,
            displayName: datasetName
          })
        );
      }

      agentDatasetsRef.current.set(entryKey, {
        compositeId,
        spatialType,
        datasetId: pending.id
      });

      return {
        dataset_entry_key: entryKey,
        status: "applied",
        total_rows: totalRows
      };
    } catch (error) {
      // The swap did not happen, so the rows the replacement was measured
      // against go back to the budget it shares with its siblings.
      rowBudget.base += previousRows;

      // A part of the layer can be on the map when the stream stops. Remove it,
      // because the agent got a failure report for this dataset.
      if (addedLayer) {
        handleRemoveDataset(addedLayer.compositeId, addedLayer.spatialType);
        rowBudget.used -= chargedRows;
      }

      return failedResult(entryKey, toWorkspaceError(rowLimitError ?? error));
    } finally {
      batchSignal.removeEventListener("abort", abortForBatch);
    }
  };

  const removeDataset = (entryKey: string): WorkspaceDatasetResult => {
    const agentDataset = agentDatasetsRef.current.get(entryKey);

    // A key this hook never applied is already absent from the map, so the
    // requested end state holds. That is not a failure, and there is nothing
    // to take off.
    if (!agentDataset)
      return { dataset_entry_key: entryKey, status: "applied" };

    try {
      handleRemoveDataset(agentDataset.compositeId, agentDataset.spatialType);
    } catch (error) {
      // The record stays, because the layer is still this hook's to remove.
      // NOTE: `handleRemoveDataset` is not transactional (it dispatches
      // before it touches the map), so a map failure here can leave the store
      // cleaned and the layer painted. The next sync reconciles it.
      return failedResult(entryKey, toWorkspaceError(error));
    }

    agentDatasetsRef.current.delete(entryKey);

    // A removal lands `applied` with no count: nothing was measured when the
    // layer came off, and the SDK refuses a count it did not earn.
    return { dataset_entry_key: entryKey, status: "applied" };
  };

  const applyDatasetChanges: SetWorkspaceDatasetsHandler = async (
    request,
    report
  ) => {
    activeBatchAbortRef.current?.abort();
    const batchController = new AbortController();
    activeBatchAbortRef.current = batchController;

    // Do the removals first. This makes each `_<n>` slot free. Thus a new
    // dataset gets `_1` and the name stays clean, for example not "Name (1)".
    // An entry in `datasets` whose key is already applied is a replacement, and
    // it holds its layer until the new version lands (see `applyDataset`).
    const removed = request.removals.map((entryKey) => {
      const result = removeDataset(entryKey);
      report(result);
      return result;
    });

    const rowBudget = { base: getExistingRowCount(), used: 0 };
    const firstPaletteIndex = getNextPaletteIndex();

    const applied = await Promise.all(
      request.datasets.map(async (proposal, position) => {
        const result = await applyDataset(
          proposal,
          firstPaletteIndex + position,
          batchController.signal,
          rowBudget
        );
        report(result);
        return result;
      })
    );

    return [...applied, ...removed];
  };

  const syncWorkspace: WorkspaceAdapter["syncWorkspace"] = async (
    active,
    { signal }
  ) => {
    const currentWorkspaceId = active?.workspaceId ?? null;
    if (currentWorkspaceId !== lastWorkspaceIdRef.current) {
      lastWorkspaceIdRef.current = currentWorkspaceId;
      clearAnalysisResult();
      drawnAnalysisIdRef.current = null;
    }

    const configuration = active?.configuration ?? null;

    // Do the working area first. It sets the map frame for the datasets.
    await syncGlobalAdmArea(configuration, signal);

    if (signal.aborted) return;

    // Do the analysis area second. Its smaller frame sets the camera last.
    await syncAreaInput(configuration, signal);

    if (signal.aborted) return;

    const desiredDatasets = configuration?.datasets ?? {};

    for (const [entryKey, agentDataset] of [...agentDatasetsRef.current]) {
      if (desiredDatasets[entryKey]) continue;

      handleRemoveDataset(agentDataset.compositeId, agentDataset.spatialType);
      agentDatasetsRef.current.delete(entryKey);
    }

    if (signal.aborted) return;

    // A key this hook already applied is only half the answer: the dataset
    // under it can have been replaced elsewhere, and the snapshot is the
    // desired state. A different id under the same key is a new version to
    // stream, not an entry that is already on the map.
    const missingDatasets = Object.values(desiredDatasets).filter((dataset) => {
      const applied = agentDatasetsRef.current.get(dataset.dataset_entry_key);
      return applied?.datasetId !== dataset.dataset_id;
    });

    if (missingDatasets.length > 0) {
      const rowBudget = { base: getExistingRowCount(), used: 0 };
      const firstPaletteIndex = getNextPaletteIndex();

      await Promise.all(
        missingDatasets.map((dataset, position) =>
          applyDataset(
            dataset as unknown as AgentDatasetProposal,
            firstPaletteIndex + position,
            signal,
            rowBudget
          )
        )
      );
    }

    if (signal.aborted) return;

    // Last, and never earlier: both the removal loop and the add above rebuild
    // `weightValues` from defaults, and a delta that only sets a weight or the
    // output analysis leaves `missingDatasets` empty — so returning early here would
    // drop exactly the change these two carry.
    syncOutputAnalysis(configuration);
    syncDatasetWeights(configuration, agentDatasetsRef.current);

    // The result draws last, and only once. In a live run the interrupt
    // already drew it, and after a refresh only this sync can.
    const analysisResult = analysisResultRef.current;
    if (analysisResult?.status !== AnalysisStatus.SUCCESS) return;
    if (analysisResult.analysis_id === drawnAnalysisIdRef.current) return;

    drawnAnalysisIdRef.current = analysisResult.analysis_id;
    await drawAnalysisResult(analysisResult.analysis_id, signal).catch(
      (error: unknown) => {
        // A newer state stopped this draw. Forget it, so the next sync draws it.
        if (signal.aborted) drawnAnalysisIdRef.current = null;
        throw error;
      }
    );
  };

  /**
   * `context.status` is always terminal `SUCCESS` here and `context.stages` may
   * be empty, so neither is read — the only thing the draw needs is the signal.
   */
  const loadAnalysisResult: NonNullable<
    WorkspaceAdapter["loadAnalysisResult"]
  > = (analysisId, { signal }) => {
    drawnAnalysisIdRef.current = analysisId;
    return drawAnalysisResult(analysisId, signal);
  };

  /**
   * Empties the workspace for a different conversation.
   *
   * The chat handlers call this function, and not the SDK. A thread change makes
   * the SDK call `syncWorkspace(null)`, but the three records of this file limit
   * that call. It does not change `map`, `analysisSummary`, `widget` or the
   * remaining `spatial` data. After a page refresh the records are empty, but
   * storage has the datasets of the previous conversation. Thus that call
   * removes nothing.
   */
  const resetWorkspace = () => {
    activeBatchAbortRef.current?.abort();
    activeBatchAbortRef.current = null;

    // Remove the layers before the slices. The ids of the layers are in the
    // `dataset` slice, and `resetAnalysisConfigSlices` empties that slice.
    handleClearDataset();
    agentDatasetsRef.current.clear();

    // Clear the records also. If not, the next sync finds these areas on the map
    // and does not add them again.
    clearAppliedGlobalAdmArea();
    clearAppliedArea();
    clearAnalysisResult();
    drawnAnalysisIdRef.current = null;

    resetAnalysisConfigSlices(dispatch);
  };

  return {
    // No memoisation. The SDK keeps the adapter in a ref and wraps the handler
    // in a callback with no dependencies. Thus a new object each render is free.
    adapter: {
      syncWorkspace,
      applyDatasetChanges,
      loadAnalysisResult
    } satisfies WorkspaceAdapter,
    resetWorkspace,
    analysisResultRef
  };
};
